
import { Component, Vue, Prop } from 'vue-property-decorator';
import SvgFrontFaceDriversLicense from '@/components/SvgFrontFaceDriversLicense.vue';
import SvgFrontFrameMyNumberCard from '@/components/SvgFrontFrameMyNumberCard.vue';
import SvgFrontFrameResidenceCard from '@/components/SvgFrontFrameResidenceCard.vue';
// 特別永住者証明書は在留カードと同一の画像コンポーネントを使用
import SvgFrontFrameSpecialPermanentResidentCertificate from '@/components/SvgFrontFrameResidenceCard.vue';
import SvgFrontFramePassport from '@/components/SvgFrontFramePassport.vue';
import SvgFrontFramePassportGlobal from '@/components/SvgFrontFramePassportGlobal.vue';
import SvgBackFrameMyNumberCard from '@/components/SvgBackFrameMyNumberCard.vue';
import SvgBackFramePassport from '@/components/SvgBackFramePassport.vue';
import SvgFrontFrameNotificationCard from '@/components/SvgFrontFrameNotificationCard.vue';
import SvgFrontFrameResidenceCertficate from '@/components/SvgFrontFrameResidenceCertificate.vue';
import CameraPageShootMessageNormal from '@/components/CameraPageShootMessageNormal.vue';
import CameraPageShootMessageGeneralBook from '@/components/CameraPageShootMessageGeneralBook.vue';
import CameraPageShootMessageGpdoc from '@/components/CameraPageShootMessageGpdoc.vue';
import CameraPageShootMessageOpdoc from '@/components/CameraPageShootMessageOpdoc.vue';
import IdDocType from '@/resources/enum/IdDocType';
import MnDocType from '@/resources/enum/MnDocType';
import { is, isPassport } from '@/util/idDoc';

@Component({
  components: {
    SvgFrontFaceDriversLicense,
    SvgFrontFrameMyNumberCard,
    SvgFrontFrameResidenceCard,
    SvgFrontFrameNotificationCard,
    SvgFrontFrameResidenceCertficate,
    SvgFrontFrameSpecialPermanentResidentCertificate,
    SvgFrontFramePassport,
    SvgFrontFramePassportGlobal,
    SvgBackFrameMyNumberCard,
    SvgBackFramePassport,
    CameraPageShootMessageNormal,
    CameraPageShootMessageGpdoc,
    CameraPageShootMessageOpdoc,
    CameraPageShootMessageGeneralBook
  }
})
export default class CameraPageShootCardFrontBack extends Vue {
  @Prop({ type: Number, required: true })
  public cardWidth!: number;

  @Prop({ type: Number, required: true })
  public cardHeight!: number;

  public get isIdDocFront(): boolean {
    return this.$store.state.camera.shootType === 'identity-document-front';
  }

  public get isIdDocBack(): boolean {
    return this.$store.state.camera.shootType === 'identity-document-back';
  }

  private get isMnDocFront(): boolean {
    return this.$store.state.camera.shootType === 'mynumber-document-front';
  }

  private get isMnDocBack(): boolean {
    return this.$store.state.camera.shootType === 'mynumber-document-back';
  }

  private get isGpDocPage(): boolean {
    return this.$store.getters['camera/isGpdoc'];
  }

  private get isOpDocPage(): boolean {
    return this.$store.getters['camera/isOpdoc'];
  }

  private get isGeneralBook(): boolean {
    return this.$store.getters['camera/isGeneralBook'];
  }

  private get isGeneralCard(): boolean {
    return this.$store.getters['camera/isGeneralCard'];
  }

  private get isA4Shape(): boolean {
    return this.$store.getters['camera/isA4Shape'];
  }

  private get isGlobal(): boolean {
    return this.$store.getters['camera/isGlobal'];
  }

  private get isForeignPassport(): boolean {
    if (is(this.$store.state.camera.idDocType, 'foreign-passport')) return true;
    return false;
  }

  private get isIdDocDriversLicense(): boolean {
    if (is(this.$store.state.camera.idDocType, 'drivers-license')) return true;
    if (is(this.$store.state.camera.idDocType, 'driving-history-certificate')) return true;

    return false;
  }

  private get isIdDocMyNumberCard(): boolean {
    if (is(this.$store.state.camera.idDocType, 'my-number-card')) return true;

    return false;
  }

  private get isIdDocResidenceCard(): boolean {
    if (is(this.$store.state.camera.idDocType, 'residence-card')) return true;

    return false;
  }

  private get isIdDocSpecialPermanentResidentCertificate(): boolean {
    if (is(this.$store.state.camera.idDocType, 'special-permanent-resident-certificate')) return true;

    return false;
  }

  private get isIdDocPassport(): boolean {
    if (isPassport(this.$store.state.camera.idDocType)) return true;

    return false;
  }

  private get isIdDocHealthInsuranceCard(): boolean {
    if (is(this.$store.state.camera.idDocType, 'health-insurance-card')) return true;

    return false;
  }

  private get isMnDocMyNumberCard(): boolean {
    return this.$store.state.camera.mnDocType === MnDocType.MyNumberCard;
  }

  private get isMnDocNotificationCard(): boolean {
    return this.$store.state.camera.mnDocType === MnDocType.NotificationCard;
  }

  private get isMnDocResidenceCertificate(): boolean {
    return this.$store.state.camera.mnDocType === MnDocType.ResidenceCertificate;
  }

  private get strokeSize(): number {
    return Math.ceil(this.cardWidth / 100);
  }

  private get cardCalcWidth(): number {
    const maxWidth: number = this.$store.state.camera.clippingDisplayWidth - this.strokeSize * 2;
    return Math.min(maxWidth, this.cardWidth);
  }

  private get cardCalcHeight(): number {
    return this.cardCalcWidth * (this.cardHeight / this.cardWidth);
  }

  private get frameSize(): { x: number; y: number; w: number; h: number } {
    const w: number = this.cardCalcWidth + this.strokeSize * 2;
    const h: number = this.cardCalcHeight + this.strokeSize * 2;
    const x: number = (this.$store.state.camera.displayWidth - w) / 2;
    const y: number = this.$store.state.camera.displayIdDocCenter - h / 2;
    return { x, y, w, h };
  }

  private get strokePath(): string[] {
    const isCard: boolean = !(this.isGpDocPage || this.isOpDocPage || this.isGeneralBook || this.isA4Shape);
    const strokeEdgeLength: number = Math.ceil(this.cardCalcWidth / (isCard ? 7 : 6));
    const strokeSideLength: number = isCard ? Math.ceil(this.cardCalcWidth / 10) : 0;
    const strokeHerfWidth: number = Math.ceil(this.strokeSize / 2);
    const x0: number = this.frameSize.x + strokeHerfWidth + strokeEdgeLength;
    const y0: number = this.frameSize.y + strokeHerfWidth;
    const x1: number = this.frameSize.x + strokeHerfWidth;
    const y1: number = this.frameSize.y - strokeHerfWidth + this.frameSize.h - strokeEdgeLength;
    const x2: number = this.frameSize.x - strokeHerfWidth + this.frameSize.w - strokeEdgeLength;
    const y2: number = this.frameSize.y - strokeHerfWidth + this.frameSize.h;
    const x3: number = this.frameSize.x - strokeHerfWidth + this.frameSize.w;
    const y3: number = this.frameSize.y + strokeHerfWidth + strokeEdgeLength;
    const x4: number = this.frameSize.x + strokeHerfWidth + (this.frameSize.w - strokeSideLength) / 2;
    const y4: number = y0;
    const x5: number = x4;
    const y5: number = y2;
    return [
      `m ${x0} ${y0} l -${strokeEdgeLength} 0 l 0 ${strokeEdgeLength}`,
      `m ${x1} ${y1} l 0 ${strokeEdgeLength} l ${strokeEdgeLength} 0`,
      `m ${x2} ${y2} l ${strokeEdgeLength} 0 l 0 -${strokeEdgeLength}`,
      `m ${x3} ${y3} l 0 -${strokeEdgeLength} l -${strokeEdgeLength} 0`,
      `m ${x4} ${y4} l ${strokeSideLength} 0`,
      `m ${x5} ${y5} l ${strokeSideLength} 0`
    ];
  }

  private get faceStyleDriversLicense(): { [key: string]: string } {
    const faceSvgWidth: number = 92;
    const faceSvgHeight: number = 114;
    const x: number = this.frameSize.x + this.strokeSize + this.cardCalcWidth * (58 / 86);
    const y: number = this.frameSize.y + this.strokeSize + this.cardCalcHeight * (16 / 54);
    const sx: number = (this.cardCalcWidth * (24 / 86)) / faceSvgWidth;
    const sy: number = (this.cardCalcHeight * (30 / 54)) / faceSvgHeight;
    const cx: number = this.frameSize.x + this.strokeSize + this.cardCalcWidth / 2;
    const isMirror: boolean = this.$store.getters['camera/isMirror'];
    return {
      transform: [
        `translateX(${cx}px) scaleX(${isMirror ? -1 : 1}) translateX(${-cx}px)`,
        `translate(${x}px, ${y}px) scale(${sx}, ${sy})`
      ].join(' ')
    };
  }

  private get frameStyle(): { [key: string]: string } {
    let svgSize: { w: number; h: number; s: number } = { w: 0, h: 0, s: 0 };
    if (this.isIdDocFront && this.isIdDocMyNumberCard) svgSize = { w: 343, h: 222, s: 4 };
    if (this.isIdDocFront && this.isIdDocResidenceCard) svgSize = { w: 343, h: 222, s: 4 };
    if (this.isIdDocFront && this.isIdDocSpecialPermanentResidentCertificate) svgSize = { w: 343, h: 222, s: 4 };
    if (this.isIdDocFront && this.isIdDocPassport) svgSize = { w: 343, h: 247, s: 4 };
    if (this.isIdDocBack && this.isIdDocMyNumberCard) svgSize = { w: 343, h: 222, s: 4 };
    if (this.isIdDocBack && this.isIdDocPassport) svgSize = { w: 343, h: 247, s: 4 };
    if (this.isMnDocFront && this.isMnDocMyNumberCard) svgSize = { w: 343, h: 222, s: 4 };
    if (this.isMnDocFront && this.isMnDocNotificationCard) svgSize = { w: 343, h: 222, s: 4 };
    if ((this.isMnDocFront && this.isMnDocResidenceCertificate) || this.isA4Shape) svgSize = { w: 291, h: 408, s: 4 };
    const svgStrokeSize: number = svgSize.s;
    const x: number = this.frameSize.x + this.strokeSize - svgStrokeSize;
    const y: number = this.frameSize.y + this.strokeSize - svgStrokeSize;
    const w: number = this.cardCalcWidth + svgStrokeSize * 2;
    const h: number = this.cardCalcHeight + svgStrokeSize * 2;
    const sx: number = w / svgSize.w;
    const sy: number = h / svgSize.h;
    const cx: number = x + w / 2;
    const isMirror: boolean = this.$store.getters['camera/isMirror'];
    return {
      transform: [
        `translateX(${cx}px) scaleX(${isMirror ? -1 : 1}) translateX(${-cx}px)`,
        `translate(${x}px, ${y}px) scale(${sx}, ${sy})`
      ].join(' ')
    };
  }
}
